/** Розыгрыш: вкладка витрины.
 *
 * Жребий тянет владелец в прямом эфире, а вкладка отвечает человеку на три
 * вопроса — «что разыгрывают», «есть ли у меня шанс» и «кто уже выиграл».
 * Главное пятно экрана — карточка приза: она продаёт участие, поэтому фото
 * крупно и описание словами владельца, а не шаблоном. Ниже — мои билеты
 * номерами, которые человек узнает, когда их назовут в эфире.
 *
 * Всё решает сервер: вкладка только показывает. Билеты приходят, когда заказ
 * выполнен, и ни одно нажатие здесь их не создаёт.
 */
import { useEffect, useState } from 'react';
import { request } from '@/shared/api';
import type { RaffleHistoryRow, RafflePrize, RaffleStageView, RaffleView } from '@/shared/types';
import { Button, Note, Skeleton } from '@/shared/ui';
import { CoinIcon } from '@/shared/coin';
import { ErrorBoundary } from '@/shared/ErrorBoundary';
import { count } from '@/shared/plural';
import { tap } from '@/shared/tg';
import { GiftIcon } from './market';
import { TicketGlyph } from './icons';

export function RaffleScreen({ onCatalog }: { onCatalog: () => void }) {
  const [view, setView] = useState<RaffleView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    request<RaffleView>('/api/raffle', {})
      .then(setView)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  if (failure) return <Note tone="warn">Розыгрыш не открылся: {failure}</Note>;
  if (!view) return <Skeleton className="h-72" />;
  if (!view.active || !view.raffle) {
    return <Note>Сейчас розыгрыша нет — следующий появится здесь.</Note>;
  }
  return (
    <ErrorBoundary title="Розыгрыш не открылся">
      <RaffleBody view={view} onCatalog={onCatalog} />
    </ErrorBoundary>
  );
}

function RaffleBody({ view, onCatalog }: { view: RaffleView; onCatalog: () => void }) {
  const raffle = view.raffle!;
  const current = view.current ?? null;
  const waiting = view.waiting ?? [];
  const queue = view.queue ?? [];
  const history = view.history ?? [];
  // Карточка приза — то, что идёт сейчас; если набор закрыт — то, что ждёт
  // эфира; а когда серия между этапами — первый из очереди
  const spotlight: RaffleStageView | null = current ?? waiting[0] ?? null;
  const upcoming = spotlight ? queue : queue.slice(1);

  return (
    <div className="screen-in">
      <h1 className="text-[22px] leading-tight font-[760] tracking-[-.02em]">{raffle.title}</h1>
      {raffle.about && (
        <p className="mt-1.5 text-[14px] leading-relaxed whitespace-pre-line text-hint">
          {raffle.about}
        </p>
      )}

      {spotlight ? (
        <PrizeCard prize={spotlight.prize} when={whenLabel(spotlight, raffle.schedule)} />
      ) : queue[0] ? (
        <PrizeCard prize={queue[0]} when="Следующий этап скоро начнётся" />
      ) : (
        <Note>Все призы серии разыграны — итоги ниже.</Note>
      )}

      {current && (
        <MyTickets
          stage={current}
          howTo={view.how_to ?? ''}
          onCatalog={onCatalog}
        />
      )}
      {!current && !waiting.length && queue.length > 0 && (
        <div className="glass mt-3 rounded-card px-4 py-3.5 text-[14px] leading-relaxed">
          <span className="font-[650]">Как получить билет. </span>
          <span className="text-hint">{view.how_to}</span>
        </div>
      )}

      {waiting.map((stage) => (
        <div key={stage.id} className="glass mt-3 rounded-card px-4 py-3.5">
          <div className="text-[13px] text-hint">Набор закрыт — ждём эфира</div>
          <div className="mt-0.5 truncate text-[15px] font-[680]">{stage.prize.title}</div>
          <div className="mt-1 text-[12px] text-hint" data-numeric>
            {count(stage.tickets, 'билет', 'билета', 'билетов')} в игре
          </div>
          {stage.mine.length > 0 && <TicketRow numbers={stage.mine} />}
        </div>
      ))}

      {upcoming.length > 0 && (
        <>
          <Heading>Дальше разыграем</Heading>
          <div className="grid gap-2">
            {upcoming.map((prize) => (
              <div key={prize.id} className="glass flex items-center gap-3 rounded-card p-2.5">
                <PrizeThumb prize={prize} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-[650]">{prize.title}</span>
                  {prize.about && (
                    <span className="block truncate text-[12px] text-hint">{prize.about}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {history.length > 0 && (
        <>
          <Heading>Уже разыграли</Heading>
          <div className="grid gap-2">
            {history.map((row) => (
              <HistoryRow key={row.id} row={row} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-6 mb-2 text-lg font-[680] tracking-[-.02em]">{children}</h2>;
}

/* --------------------------------------------------------------- приз */

/** Карточка приза — главное пятно экрана: она продаёт участие. */
function PrizeCard({ prize, when }: { prize: RafflePrize; when: string }) {
  return (
    <div className="balance-card mt-4 overflow-hidden rounded-layer">
      {prize.image ? (
        <img
          src={prize.image}
          alt=""
          className="block aspect-[4/3] w-full object-cover"
          loading="eager"
        />
      ) : (
        // Без фото — пьедестал с символом приза: пустой прямоугольник
        // вместо картинки читался бы как недогрузившаяся страница
        <div className="grid aspect-[16/9] w-full place-items-center">
          <span className="tile-pedestal grid size-28 place-items-center rounded-full">
            {prize.kind === 'coins' ? <CoinIcon size={62} /> : <GiftIcon size={60} />}
          </span>
        </div>
      )}
      <div className="p-4">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1 text-[12px] font-[680] text-gold-2">
          <TicketGlyph size={14} />
          {when}
        </div>
        <div className="mt-2 text-[20px] leading-tight font-[740] tracking-[-.01em]">
          {prize.title}
        </div>
        {prize.kind === 'coins' && prize.coins > 0 && (
          <div className="mt-1 flex items-center gap-1.5 text-[14px] font-[680] text-gold-2" data-numeric>
            <CoinIcon size={16} />
            {count(prize.coins, 'паркоин', 'паркоина', 'паркоинов')} сразу на баланс
          </div>
        )}
        {prize.about && (
          <p className="mt-1.5 text-[14px] leading-relaxed whitespace-pre-line text-hint">
            {prize.about}
          </p>
        )}
      </div>
    </div>
  );
}

/** Миниатюра приза: с фото и без — одного размера, чтобы очередь была ровной. */
function PrizeThumb({ prize }: { prize: RafflePrize }) {
  return (
    <span className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-xl bg-glass-2">
      {prize.thumb ? (
        <img src={prize.thumb} alt="" className="size-full object-cover" loading="lazy" />
      ) : prize.kind === 'coins' ? (
        <CoinIcon size={28} />
      ) : (
        <GiftIcon size={28} />
      )}
    </span>
  );
}

/* ------------------------------------------------------------ билеты */

function MyTickets({
  stage,
  howTo,
  onCatalog,
}: {
  stage: RaffleStageView;
  howTo: string;
  onCatalog: () => void;
}) {
  const total = (
    <span className="text-[12px] text-hint" data-numeric>
      В игре {count(stage.tickets, 'билет', 'билета', 'билетов')}
    </span>
  );
  if (stage.mine.length) {
    return (
      <div className="glass mt-3 rounded-card px-4 py-3.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[15px] font-[680]">
            {stage.mine.length === 1 ? 'Мой билет' : 'Мои билеты'}
          </span>
          {total}
        </div>
        <TicketRow numbers={stage.mine} />
        <div className="mt-2 text-[12px] text-hint">
          Назовут ваш номер в эфире — приз ваш. Больше покупок — больше билетов.
        </div>
      </div>
    );
  }
  return (
    <div className="glass mt-3 rounded-card px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[15px] font-[680]">Как получить билет</span>
        {total}
      </div>
      <p className="mt-1 text-[14px] leading-relaxed text-hint">{howTo}</p>
      <Button
        className="mt-3 w-full"
        onClick={() => {
          tap();
          onCatalog();
        }}
      >
        В каталог
      </Button>
    </div>
  );
}

/** Номера билетов — крупно, как на самом билете: их слушают в эфире. */
function TicketRow({ numbers }: { numbers: number[] }) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-2">
      {numbers.map((number) => (
        <span
          key={number}
          className="raffle-ticket inline-flex items-baseline gap-0.5 px-4 py-2 text-[22px] leading-none font-[780] text-gold-ink"
          data-numeric
        >
          <span className="text-[12px] font-[700] opacity-70">№</span>
          {number}
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------ история */

function HistoryRow({ row }: { row: RaffleHistoryRow }) {
  if (row.status === 'skipped') {
    return (
      <div className="glass flex items-center gap-3 rounded-card p-2.5 opacity-70">
        <PrizeThumb prize={row.prize} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-[620]">{row.prize.title}</span>
          <span className="block text-[12px] text-hint">
            Этап пропущен — приз разыграем в следующем
          </span>
        </span>
      </div>
    );
  }
  const status = row.mine
    ? row.prize.kind === 'coins'
      ? 'Паркоины зачислены на баланс'
      : row.handed
        ? 'Приз вручён'
        : 'Менеджер свяжется для вручения'
    : null;
  return (
    <div
      className={`flex items-center gap-3 rounded-card border p-2.5 ${
        row.mine ? 'border-gold/40 bg-gold/12' : 'glass border-transparent'
      }`}
    >
      <PrizeThumb prize={row.prize} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-[650]">{row.prize.title}</span>
        <span className="block truncate text-[12px] text-hint" data-numeric>
          Билет №{row.number}
          {row.winner ? ` · ${row.winner}` : ''}
          {row.at ? ` · ${shortDate(row.at)}` : ''}
        </span>
        {status && (
          <span className="mt-0.5 block text-[12px] font-[680] text-gold-2">
            Ваш выигрыш · {status}
          </span>
        )}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------ сроки */

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function shortDate(stamp: string): string {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}`;
}

/** Когда разыграют — словами. Граница окна — полночь после последнего дня
    набора, поэтому считаем от секунды до неё: иначе «сегодня» стало бы
    «завтра». */
function whenLabel(stage: RaffleStageView, schedule: string): string {
  if (stage.status === 'waiting') return 'Набор закрыт — итоги скоро';
  if (schedule === 'manual' || !stage.closes_at) return 'Дату розыгрыша объявим в канале';
  const last = new Date(new Date(stage.closes_at).getTime() - 1000);
  if (Number.isNaN(last.getTime())) return 'Дату розыгрыша объявим в канале';
  const today = new Date();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (schedule === 'week') {
    return last.getDay() === 0 && last.getTime() - today.getTime() < 7 * 86400_000
      ? 'Билеты до воскресенья'
      : `Билеты до ${shortDate(last.toISOString())}`;
  }
  if (sameDay(last, today)) return 'Разыгрываем сегодня';
  if (sameDay(last, tomorrow)) return 'Разыгрываем завтра';
  return `Разыгрываем ${shortDate(last.toISOString())}`;
}
